import React, { useState, useEffect } from 'react';
import useDebounce from '../../hooks/useDebounce'; // kept — may be used by child pages
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../contexts/ToastContext';
import { useNavigate } from 'react-router-dom';
import { leadsAPI, analyticsAPI, tasksAPI, opportunitiesAPI } from '../../services/api';
import AdminSidebar from '../../components/AdminSidebar';
import LanguageToggle from '../../components/LanguageToggle';
import {
  Users, RefreshCw, LogOut, AlertCircle,
  Euro, Package, FileText,
  ArrowUpRight, ArrowDownRight, ExternalLink,
  CheckSquare, Target, TrendingUp, Check
} from 'lucide-react';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const { t } = useTranslation();
  const { admin, logout } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  const [leads, setLeads] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [todayTasks, setTodayTasks] = useState([]);
  const [forecast, setForecast] = useState(null);

  // Compute today's new leads count from the leads list
  const newTodayCount = leads.filter(lead => {
    const today = new Date();
    const leadDate = new Date(lead.createdAt);
    return (
      leadDate.getFullYear() === today.getFullYear() &&
      leadDate.getMonth() === today.getMonth() &&
      leadDate.getDate() === today.getDate()
    );
  }).length;

  // Revenue trend vs last month
  const revenueThisMonth = analytics?.orders?.revenueThisMonth ?? 0;
  const revenueLastMonth = analytics?.orders?.revenueLastMonth ?? 0;
  const revenueTrendPct =
    revenueLastMonth > 0
      ? (((revenueThisMonth - revenueLastMonth) / revenueLastMonth) * 100).toFixed(1)
      : null;
  const revenueUp = revenueTrendPct !== null && Number(revenueTrendPct) >= 0;

  // Load data once on mount; manual refresh via button
  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const [leadsRes, analyticsRes] = await Promise.all([
        leadsAPI.getAll({}),
        analyticsAPI.getOverview()
      ]);
      setLeads(leadsRes.data.leads);
      setAnalytics(analyticsRes.data.analytics);
      // Load CRM widgets independently (fail gracefully if backend not yet deployed)
      try {
        const [tasksRes, forecastRes] = await Promise.all([
          tasksAPI.getToday(),
          opportunitiesAPI.getForecast(),
        ]);
        setTodayTasks(tasksRes.data.tasks || []);
        setForecast(forecastRes.data || null);
      } catch { /* CRM data not yet available — silently skip */ }
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  // Kept — used by AdminLeads and potential inline lead status changes
  const handleStatusChange = async (leadId, newStatus) => {
    try {
      await leadsAPI.update(leadId, { status: newStatus });
      loadDashboard();
    } catch (error) {
      toast.error(t('admin.leads.errorUpdate'));
    }
  };

  const handleDelete = async (leadId) => {
    if (!window.confirm(t('admin.leads.deleteConfirm'))) return;
    try {
      await leadsAPI.delete(leadId);
      loadDashboard();
    } catch (error) {
      toast.error(t('admin.leads.errorDelete'));
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      new: '#3b82f6',
      contacted: '#8b5cf6',
      quoted: '#f59e0b',
      converted: '#10b981',
      closed: '#6b7280',
      lost: '#ef4444'
    };
    return colors[status] || '#6b7280';
  };

  const getStatusLabel = (status) => {
    return t(`admin.dashboard.statusLabels.${status}`, status);
  };

  const getUrgencyBadge = (urgency) => {
    const badges = {
      urgent: { emoji: '🔥', color: '#ef4444' },
      high: { emoji: '⚡', color: '#f97316' },
      normal: { emoji: '📦', color: '#3b82f6' },
      low: { emoji: '📋', color: '#6b7280' }
    };
    return badges[urgency] || badges.normal;
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateShort = (date) => {
    return new Date(date).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatCurrency = (val) => {
    if (!val && val !== 0) return '€ —';
    return (
      '€\u00a0' +
      Number(val).toLocaleString('it-IT', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    );
  };

  // Alert counts
  const overdueOrders = analytics?.orders?.overdueOrders ?? 0;
  const expiringQuotes = analytics?.quotes?.expiringQuotes ?? 0;
  const hasAlerts = overdueOrders > 0 || expiringQuotes > 0;

  if (loading && !analytics) {
    return (
      <div className="admin-loading">
        <RefreshCw className="spin" size={40} />
        <p>{t('admin.common.loading')}</p>
      </div>
    );
  }

  return (
    <div className="admin-layout">
      <AdminSidebar />

      <div className="admin-content">

        {/* ── Header ───────────────────────────────────────────── */}
        <header className="content-header">
          <div>
            <h1>{t('admin.dashboard.title')}</h1>
            <p>{t('admin.dashboard.welcome')}, {admin?.name}</p>
          </div>
          <div className="admin-header-right">
            <LanguageToggle />
            <button onClick={loadDashboard} className="admin-btn admin-btn-secondary">
              <RefreshCw size={18} />
            </button>
            <button onClick={logout} className="admin-btn admin-btn-danger">
              <LogOut size={18} />
              {t('admin.dashboard.actions.logout')}
            </button>
          </div>
        </header>

        {/* ── Section 1: KPI Cards ─────────────────────────────── */}
        <div className="admin-stats">

          {/* 1. Ricavi Questo Mese */}
          <div className="stat-card">
            <div className="stat-icon" style={{ background: 'rgba(16, 185, 129, 0.12)' }}>
              <Euro size={24} color="#10b981" />
            </div>
            <div className="stat-content">
              <p className="stat-label">{t('admin.dashboard.kpi.revenue')}</p>
              <h2 className="stat-value" style={{ fontSize: '1.6rem' }}>
                {formatCurrency(revenueThisMonth)}
              </h2>
              {revenueTrendPct !== null && (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 3,
                    fontSize: 13,
                    fontWeight: 700,
                    marginTop: 4,
                    color: revenueUp ? '#10b981' : '#ef4444',
                    background: revenueUp
                      ? 'rgba(16,185,129,0.12)'
                      : 'rgba(239,68,68,0.12)',
                    borderRadius: 6,
                    padding: '2px 8px'
                  }}
                >
                  {revenueUp
                    ? <ArrowUpRight size={14} />
                    : <ArrowDownRight size={14} />}
                  {revenueUp ? '+' : ''}{revenueTrendPct}% {t('admin.dashboard.kpi.vsLastMonth')}
                </span>
              )}
            </div>
          </div>

          {/* 2. Ordini Attivi */}
          <div className="stat-card">
            <div className="stat-icon" style={{ background: 'rgba(249, 115, 22, 0.12)' }}>
              <Package size={24} color="#f97316" />
            </div>
            <div className="stat-content">
              <p className="stat-label">{t('admin.dashboard.kpi.activeOrders')}</p>
              <h2 className="stat-value">
                {analytics?.orders?.activeProductionOrders ?? 0}
              </h2>
              <span style={{ fontSize: 12, color: '#71717a', marginTop: 4, display: 'block' }}>
                {t('admin.dashboard.kpi.inProductionQC')}
              </span>
            </div>
          </div>

          {/* 3. Preventivi in Attesa */}
          <div className="stat-card">
            <div className="stat-icon" style={{ background: 'rgba(59, 130, 246, 0.12)' }}>
              <FileText size={24} color="#3b82f6" />
            </div>
            <div className="stat-content">
              <p className="stat-label">{t('admin.dashboard.kpi.pendingQuotes')}</p>
              <h2 className="stat-value">
                {analytics?.quotes?.pendingQuotes ?? 0}
              </h2>
              <span style={{ fontSize: 12, color: '#71717a', marginTop: 4, display: 'block' }}>
                {t('admin.dashboard.kpi.pendingQuotesHint')}
              </span>
            </div>
          </div>

          {/* 4. Nuovi Lead Oggi */}
          <div className="stat-card">
            <div className="stat-icon" style={{ background: 'rgba(139, 92, 246, 0.12)' }}>
              <Users size={24} color="#8b5cf6" />
            </div>
            <div className="stat-content">
              <p className="stat-label">{t('admin.dashboard.kpi.newLeadsToday')}</p>
              <h2 className="stat-value">{newTodayCount}</h2>
              <span style={{ fontSize: 12, color: '#71717a', marginTop: 4, display: 'block' }}>
                {t('admin.dashboard.kpi.receivedToday')}
              </span>
            </div>
          </div>

        </div>

        {/* ── Section 2: Alerts Panel ──────────────────────────── */}
        {hasAlerts && (
          <div
            style={{
              background: 'rgba(249,115,22,0.06)',
              border: '1.5px solid rgba(249,115,22,0.45)',
              borderRadius: 12,
              padding: '20px 24px',
              marginBottom: 28
            }}
          >
            <h3 style={{ margin: '0 0 14px 0', fontSize: 16, color: '#fb923c', display: 'flex', alignItems: 'center', gap: 8 }}>
              <AlertCircle size={18} color="#fb923c" />
              ⚠ {t('admin.dashboard.alerts.title')}
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {overdueOrders > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <AlertCircle size={16} color="#f97316" style={{ flexShrink: 0 }} />
                  <span style={{ color: '#e4e4e7', flex: 1 }}>
                    <strong style={{ color: '#fb923c' }}>{overdueOrders}</strong>{' '}
                    {overdueOrders === 1
                      ? t('admin.dashboard.alerts.overdueOrder')
                      : t('admin.dashboard.alerts.overdueOrders')}
                  </span>
                  <button
                    onClick={() => navigate('/admin/orders')}
                    className="admin-btn admin-btn-secondary"
                    style={{ padding: '6px 14px', fontSize: 13 }}
                  >
                    {t('admin.dashboard.alerts.viewOrders')} <ExternalLink size={13} style={{ marginLeft: 4 }} />
                  </button>
                </div>
              )}
              {expiringQuotes > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <AlertCircle size={16} color="#f97316" style={{ flexShrink: 0 }} />
                  <span style={{ color: '#e4e4e7', flex: 1 }}>
                    <strong style={{ color: '#fb923c' }}>{expiringQuotes}</strong>{' '}
                    {expiringQuotes === 1
                      ? t('admin.dashboard.alerts.expiringQuote')
                      : t('admin.dashboard.alerts.expiringQuotes')}
                  </span>
                  <button
                    onClick={() => navigate('/admin/quotes')}
                    className="admin-btn admin-btn-secondary"
                    style={{ padding: '6px 14px', fontSize: 13 }}
                  >
                    {t('admin.dashboard.alerts.viewQuotes')} <ExternalLink size={13} style={{ marginLeft: 4 }} />
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Section 3: Quick Actions ─────────────────────────── */}
        <div
          style={{
            display: 'flex',
            gap: 12,
            flexWrap: 'wrap',
            marginBottom: 32
          }}
        >
          <button
            className="admin-btn admin-btn-primary"
            onClick={() => navigate('/admin/quotes')}
          >
            <FileText size={17} />
            {t('admin.dashboard.actions.newQuote')}
          </button>
          <button
            className="admin-btn admin-btn-secondary"
            onClick={() => navigate('/admin/orders')}
          >
            <Package size={17} />
            {t('admin.dashboard.actions.newOrder')}
          </button>
          <button
            className="admin-btn admin-btn-secondary"
            onClick={() => navigate('/admin/leads')}
          >
            <Users size={17} />
            {t('admin.dashboard.actions.viewLeads')}
          </button>
        </div>

        {/* ── Section 4: Recent Activity ───────────────────────── */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
            gap: 20
          }}
        >
          {/* Left column — Ultimi Lead */}
          <div
            className="admin-table-container"
            style={{ padding: 0 }}
          >
            <div className="admin-table-header">
              <h2 style={{ fontSize: 16, margin: 0 }}>{t('admin.common.recentLeads')}</h2>
              <button
                className="admin-btn admin-btn-secondary"
                style={{ padding: '6px 14px', fontSize: 13 }}
                onClick={() => navigate('/admin/leads')}
              >
                {t('admin.common.viewAll')}
              </button>
            </div>

            {(analytics?.recentLeads ?? leads.slice(0, 5)).length === 0 ? (
              <div className="admin-empty" style={{ padding: '32px 20px' }}>
                <AlertCircle size={36} />
                <p style={{ fontSize: 14 }}>{t('admin.leads.noRecentLeads')}</p>
              </div>
            ) : (
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>{t('admin.leads.colName')}</th>
                    <th>{t('admin.leads.colStatus')}</th>
                    <th>{t('admin.leads.colDate')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(analytics?.recentLeads ?? leads.slice(0, 5)).map((lead) => (
                    <tr key={lead._id}>
                      <td>
                        <span style={{ fontWeight: 600, color: '#e4e4e7' }}>
                          {lead.name}
                        </span>
                      </td>
                      <td>
                        <span
                          style={{
                            display: 'inline-block',
                            padding: '3px 10px',
                            borderRadius: 6,
                            fontSize: 12,
                            fontWeight: 700,
                            background: `${getStatusColor(lead.status)}22`,
                            color: getStatusColor(lead.status)
                          }}
                        >
                          {getStatusLabel(lead.status)}
                        </span>
                      </td>
                      <td style={{ color: '#71717a', fontSize: 13 }}>
                        {formatDateShort(lead.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Right column — CRM Widgets */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* Today's Tasks widget */}
            <div className="admin-table-container" style={{ padding: 0 }}>
              <div className="admin-table-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 style={{ fontSize: 16, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <CheckSquare size={18} color="#f59e0b" /> Today's Tasks
                  {todayTasks.length > 0 && (
                    <span style={{ padding: '1px 8px', borderRadius: 10, background: '#f59e0b20', color: '#f59e0b', fontSize: 12, fontWeight: 700 }}>
                      {todayTasks.filter(t => !t.isCompleted).length}
                    </span>
                  )}
                </h2>
                <button
                  onClick={() => navigate('/admin/tasks')}
                  style={{ background: 'none', border: 'none', color: '#71717a', cursor: 'pointer', fontSize: 12 }}
                >
                  View all →
                </button>
              </div>
              {todayTasks.length === 0 ? (
                <div style={{ padding: '24px 20px', textAlign: 'center', color: '#52525b', fontSize: 13 }}>
                  🎉 All clear for today!
                </div>
              ) : (
                <div style={{ padding: '8px 16px 16px' }}>
                  {todayTasks.slice(0, 5).map(task => {
                    const overdue = task.dueAt && !task.isCompleted && new Date(task.dueAt) < new Date();
                    return (
                      <div key={task._id} style={{ display: 'flex', gap: 10, padding: '8px 0', borderBottom: '1px solid #1c1c1c', alignItems: 'flex-start' }}>
                        <div style={{
                          width: 20, height: 20, borderRadius: '50%', flexShrink: 0, marginTop: 2,
                          border: `2px solid ${task.isCompleted ? '#10b981' : overdue ? '#ef4444' : '#27272a'}`,
                          background: task.isCompleted ? '#10b98120' : 'transparent',
                          display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}>
                          {task.isCompleted && <Check size={10} color="#10b981" />}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, color: task.isCompleted ? '#52525b' : '#fff', textDecoration: task.isCompleted ? 'line-through' : 'none', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {task.title}
                          </div>
                          {task.dueAt && (
                            <div style={{ fontSize: 11, color: overdue ? '#ef4444' : '#52525b', marginTop: 1 }}>
                              {overdue ? '⚠ Overdue — ' : ''}
                              {new Date(task.dueAt).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                            </div>
                          )}
                        </div>
                        <span style={{
                          fontSize: 11, padding: '1px 6px', borderRadius: 6, flexShrink: 0,
                          background: task.priority === 'high' ? '#ef444420' : '#f59e0b20',
                          color: task.priority === 'high' ? '#ef4444' : '#f59e0b',
                          fontWeight: 600
                        }}>{task.priority}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Pipeline Forecast widget */}
            {forecast && (
              <div className="admin-table-container" style={{ padding: 0 }}>
                <div className="admin-table-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h2 style={{ fontSize: 16, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Target size={18} color="#f97316" /> Pipeline Forecast
                  </h2>
                  <button
                    onClick={() => navigate('/admin/pipeline')}
                    style={{ background: 'none', border: 'none', color: '#71717a', cursor: 'pointer', fontSize: 12 }}
                  >
                    View Kanban →
                  </button>
                </div>
                <div style={{ padding: '12px 20px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {[
                    { label: 'Pipeline Value', value: `€${(forecast.pipelineValue || 0).toFixed(0)}`, color: '#3b82f6' },
                    { label: 'Weighted Value', value: `€${(forecast.weightedValue || 0).toFixed(0)}`, color: '#8b5cf6' },
                    { label: 'Closing This Month', value: `€${(forecast.expectedThisMonth || 0).toFixed(0)}`, color: '#10b981' },
                  ].map(f => (
                    <div key={f.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #1c1c1c' }}>
                      <span style={{ fontSize: 13, color: '#a1a1aa' }}>{f.label}</span>
                      <span style={{ fontSize: 15, fontWeight: 700, color: f.color }}>{f.value}</span>
                    </div>
                  ))}
                  {(forecast.byStage || []).length > 0 && (
                    <div style={{ marginTop: 4 }}>
                      {forecast.byStage.map(s => (
                        <div key={s.stage} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#52525b', padding: '2px 0' }}>
                          <span>{s.stage}</span>
                          <span>{s.count} deals · €{(s.total || 0).toFixed(0)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

        </div>

      </div>
    </div>
  );
};

export default AdminDashboard;
